/**
 * The /api/chat response contract, shared by the route and its only client
 * (useVectorChat).
 *
 * Every return path in the route goes through this type — POST's return type
 * is pinned to NextResponse<ChatResponse>, so an ad-hoc shape fails
 * type-check instead of reaching the browser. The client parses with
 * parseChatResponse and switches exhaustively on `kind` and on the error
 * code, so a new variant it does not handle also fails type-check.
 *
 * Why this exists: the route used to return five different shapes and the
 * client read one of them. The email gate, spam warnings, disqualification
 * exits and every refusal with user-facing copy (blocked, AI disabled,
 * message too long) rendered as a fake "trouble connecting" error with a
 * Retry button, so gated visitors never saw the ask for a work email.
 *
 * Pure module (no I/O) so both sides can import it.
 */

export const CHAT_ERROR_CODES = [
  'missing_fields',
  'ai_disabled',
  'message_too_long',
  'ip_unresolved',
  'visitor_blocked',
  'rate_limit_exceeded',
  'session_not_found',
  'spam_detected',
  'message_limit_reached',
  'internal_error',
] as const

export type ChatErrorCode = (typeof CHAT_ERROR_CODES)[number]

/**
 * Copy for a turn that failed for no reason the visitor can act on: a server
 * error, or a network/parse failure on the client. Never used for a turn the
 * server deliberately refused — those carry their own message.
 */
export const CHAT_FAILURE_MESSAGE =
  "I'm sorry, I'm having trouble connecting right now. Please try again in a moment."

export type ChatResponse =
  /** An assistant message to show: an answer, a spam warning, a disqualification exit, or the email gate. */
  | { kind: 'reply'; text: string }
  /** A hard-stopped session: the message was received and deliberately not answered. */
  | { kind: 'silent' }
  /** A refused turn. `message` is user-facing copy the client renders verbatim. */
  | { kind: 'error'; error: ChatErrorCode; message: string }

/**
 * What the route sends: ChatResponse, except a reply also carries
 * `response`, the field the pre-contract client read. Tabs loaded before
 * this contract shipped keep that bundle until they refresh; without the
 * alias their next answer renders as the connection error.
 * parseChatResponse ignores it. Remove once no pre-contract bundle (before
 * 2026-09-21) can still be open.
 */
export type ChatResponseBody =
  | (Extract<ChatResponse, { kind: 'reply' }> & { response: string })
  | Exclude<ChatResponse, { kind: 'reply' }>

export function toChatResponseBody(body: ChatResponse): ChatResponseBody {
  return body.kind === 'reply' ? { ...body, response: body.text } : body
}

/**
 * Parses an /api/chat response body. Returns null for anything outside the
 * contract (an HTML error page, a truncated body, a stale deploy) so the
 * caller treats it as a transport failure rather than guessing at fields.
 */
export function parseChatResponse(body: unknown): ChatResponse | null {
  if (typeof body !== 'object' || body === null) return null
  const value = body as Record<string, unknown>

  switch (value.kind) {
    case 'reply':
      return typeof value.text === 'string' && value.text.length > 0
        ? { kind: 'reply', text: value.text }
        : null
    case 'silent':
      return { kind: 'silent' }
    case 'error':
      return isChatErrorCode(value.error) &&
        typeof value.message === 'string' &&
        value.message.length > 0
        ? { kind: 'error', error: value.error, message: value.message }
        : null
    default:
      return null
  }
}

function isChatErrorCode(value: unknown): value is ChatErrorCode {
  return (CHAT_ERROR_CODES as readonly unknown[]).includes(value)
}
